/**
 * RitualAIInsights - AI-powered insights and recommendations for rituals
 * Uses Therappi AI to analyse ritual patterns and provide personalised guidance
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Sparkles, RefreshCw, Brain, Lightbulb, ChevronDown, ChevronUp
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useRitualWhoopCorrelation } from '@/hooks/useRitualWhoopCorrelation';
import { cn } from '@/lib/utils';
import { format, subDays } from 'date-fns';

interface RitualAIInsightsProps {
  className?: string;
}

interface AIInsight {
  title: string;
  insight: string;
  recommendation: string;
  type: 'encouragement' | 'pattern' | 'suggestion' | 'warning';
}

export const RitualAIInsights: React.FC<RitualAIInsightsProps> = ({ className }) => {
  const [expanded, setExpanded] = useState(false);
  const { data: whoopCorrelation } = useRitualWhoopCorrelation(30);

  const { data: insights, isLoading, refetch, isFetching } = useQuery<AIInsight[]>({
    queryKey: ['ritual-ai-insights'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      // Fetch ritual data for analysis
      const startDate = format(subDays(new Date(), 30), 'yyyy-MM-dd');
      
      const { data: ritualLogs } = await supabase
        .from('daily_ritual_logs')
        .select('log_date, routine_id')
        .eq('user_id', user.id)
        .gte('log_date', startDate)
        .order('log_date', { ascending: false });

      // Get routine types for the logs
      const routineIds = [...new Set((ritualLogs || []).map(l => l.routine_id))];
      const { data: routines } = await supabase
        .from('user_ritual_routines')
        .select('id, routine_type')
        .in('id', routineIds.length > 0 ? routineIds : ['']);

      const routineTypeMap = new Map((routines || []).map(r => [r.id, r.routine_type]));

      // Generate insights based on data patterns
      const insights: AIInsight[] = [];
      
      const morningRituals = (ritualLogs || []).filter(l => routineTypeMap.get(l.routine_id) === 'morning');
      const eveningRituals = (ritualLogs || []).filter(l => routineTypeMap.get(l.routine_id) === 'evening');
      const totalRituals = (ritualLogs || []).length;
      
      // Streak insight
      if (morningRituals.length >= 7) {
        insights.push({
          title: "Morning Momentum",
          insight: `You've completed ${morningRituals.length} morning habits this month. This consistency is building powerful neural pathways.`,
          recommendation: "Keep this momentum going - your brain is forming a lasting habit.",
          type: 'encouragement'
        });
      } else if (morningRituals.length < 3 && totalRituals > 0) {
        insights.push({
          title: "Morning Opportunity",
          insight: "Starting your day with intention sets the tone for everything that follows.",
          recommendation: "Try setting a gentle reminder 30 minutes after your usual wake time.",
          type: 'suggestion'
        });
      }

      // Evening ritual insight
      if (eveningRituals.length >= 5 && whoopCorrelation?.sleepImprovementPercent && whoopCorrelation.sleepImprovementPercent > 0) {
        insights.push({
          title: "Sleep Connection",
          insight: `Your evening habits are positively impacting your sleep quality. Your WHOOP shows ${whoopCorrelation.sleepImprovementPercent}% better sleep after evening habits.`,
          recommendation: "Continue prioritising your wind-down routine - your body is responding well.",
          type: 'pattern'
        });
      }

      // Mood pattern insight based on ritual count
      if (totalRituals >= 10) {
        insights.push({
          title: "Building Consistency",
          insight: `You've logged ${totalRituals} habit sessions this month. Regular habits support emotional regulation and nervous system health.`,
          recommendation: "Consider journaling about what specific activities feel most impactful.",
          type: 'pattern'
        });
      }

      // Recovery insight from WHOOP
      if (whoopCorrelation?.recoveryImprovementPercent && whoopCorrelation.recoveryImprovementPercent > 5) {
        insights.push({
          title: "Recovery Boost",
          insight: `Days with healthy habits show ${whoopCorrelation.recoveryImprovementPercent}% higher recovery scores. Your nervous system is benefiting from these practices.`,
          recommendation: "You're doing great - consistent habits are measurably improving your physiological recovery.",
          type: 'pattern'
        });
      }

      // Balance recommendation
      if (morningRituals.length > eveningRituals.length * 2) {
        insights.push({
          title: "Balance Check",
          insight: "You're stronger with morning habits than evening. Both bookend practices support circadian rhythm and emotional regulation.",
          recommendation: "Try setting a phone-free zone 30 minutes before bed as a gentle evening habit start.",
          type: 'suggestion'
        });
      }

      // Default encouragement if no insights
      if (insights.length === 0) {
        insights.push({
          title: "Begin Your Journey",
          insight: "Healthy habits are small acts of self-care that compound over time. Each completion builds resilience and self-trust.",
          recommendation: "Start with just one activity that feels meaningful to you. Consistency matters more than perfection.",
          type: 'encouragement'
        });
      }

      return insights;
    },
    staleTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
  });

  if (isLoading || !insights || insights.length === 0) {
    return null;
  }

  const primaryInsight = insights[0];
  const additionalInsights = insights.slice(1);

  const typeConfig = {
    encouragement: { icon: Sparkles, color: 'text-primary', bg: 'bg-primary/10' },
    pattern: { icon: Brain, color: 'text-secondary', bg: 'bg-secondary/10' },
    suggestion: { icon: Lightbulb, color: 'text-accent', bg: 'bg-accent/10' },
    warning: { icon: Brain, color: 'text-destructive', bg: 'bg-destructive/10' },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={className}
    >
      <Card className="border-border/50 overflow-hidden">
        <CardContent className="p-4 space-y-3">
          {/* Primary insight */}
          <div className="flex items-start gap-3">
            <div className={cn(
              "w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
              typeConfig[primaryInsight.type].bg
            )}>
              {React.createElement(typeConfig[primaryInsight.type].icon, {
                className: cn("w-5 h-5", typeConfig[primaryInsight.type].color)
              })}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-medium text-foreground">
                  {primaryInsight.title}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => refetch()}
                  disabled={isFetching}
                  className="h-6 w-6 p-0"
                >
                  <RefreshCw className={cn("w-3 h-3", isFetching && "animate-spin")} />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                {primaryInsight.insight}
              </p>
              <p className="text-xs text-foreground/80 italic">
                💡 {primaryInsight.recommendation}
              </p>
            </div>
          </div>

          {/* Expand for more insights */}
          {additionalInsights.length > 0 && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded(!expanded)}
                className="w-full justify-between text-xs text-muted-foreground hover:text-foreground"
              >
                <span>{additionalInsights.length} more insights</span>
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>

              <AnimatePresence>
                {expanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="space-y-3 overflow-hidden"
                  >
                    {additionalInsights.map((insight, index) => (
                      <div key={index} className="flex items-start gap-3 pt-3 border-t border-border/30">
                        <div className={cn(
                          "w-8 h-8 rounded-lg flex items-center justify-center shrink-0",
                          typeConfig[insight.type].bg
                        )}>
                          {React.createElement(typeConfig[insight.type].icon, {
                            className: cn("w-4 h-4", typeConfig[insight.type].color)
                          })}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground mb-1">
                            {insight.title}
                          </p>
                          <p className="text-xs text-muted-foreground mb-1">
                            {insight.insight}
                          </p>
                          <p className="text-xs text-foreground/80 italic">
                            💡 {insight.recommendation}
                          </p>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default RitualAIInsights;
